import * as Sentry from '@sentry/angular';
import {
  ApplicationConfig,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
  isDevMode,
  makeEnvironmentProviders,
  provideAppInitializer,
  inject,
  ErrorHandler,
} from '@angular/core';
import {
  provideRouter,
  withComponentInputBinding,
  Router,
} from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideOAuthClient } from 'angular-oauth2-oidc';
import { provideOptimus } from '@openng/optimus-ui/config';
import Aura from '@openng/optimus-ui-themes/aura';

import { routes } from './app.routes';
import { TranslocoLanguageLoader } from './transloco-loader';
import {
  provideTransloco,
  provideTranslocoLoader,
  TranslocoService,
} from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';
import { authInterceptor } from './core/services/auth-interceptor';
import { statusMessageInterceptor } from './core/services/status-message-interceptor';
import { MessageService } from '@openng/optimus-ui/api';
import {
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGES,
} from './core/services/language-service';
import { AuthAccess } from './core/services/auth-access';
import { FRIENDLY_CAPTCHA_SDK } from './shared/services/friendly-captcha.token';
import { FriendlyCaptchaSDK } from '@friendlycaptcha/sdk';
import { loadingInterceptor } from './core/services/loading-interceptor';
import { companyInterceptor } from './shared/services/company-interceptor';
import { provideSignalFormsConfig } from '@angular/forms/signals';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(routes, withComponentInputBinding()),
    provideAnimationsAsync(),
    provideHttpClient(
      withInterceptors([
        authInterceptor,
        companyInterceptor,
        loadingInterceptor,
        statusMessageInterceptor,
      ])
    ),
    provideOAuthClient(),
    provideAppInitializer(() => {
      const authAccess = inject(AuthAccess);
      return authAccess.runInitialLoginSequence();
    }),
    provideOptimus({
      theme: {
        preset: Aura,
        options: {
          darkModeSelector: 'none',
          cssLayer: {
            name: 'optimus',
            order: 'app-styles, optimus',
          },
        },
      },
    }),
    MessageService,
    provideTransloco({
      config: {
        availableLangs: [...SUPPORTED_LANGUAGES],
        defaultLang: DEFAULT_LANGUAGE,
        // Remove this option if your application doesn't support changing language in runtime.
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoLanguageLoader,
    }),
    makeEnvironmentProviders([
      provideTranslocoLoader(TranslocoLanguageLoader),
      provideAppInitializer(() =>
        firstValueFrom(inject(TranslocoService).load(DEFAULT_LANGUAGE))
      ),
    ]),
    {
      provide: FRIENDLY_CAPTCHA_SDK,
      useFactory: () => new FriendlyCaptchaSDK(),
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    }),
    provideSignalFormsConfig({
      classes: {
        // Marks fields that are invalid but have not been touched yet.
        // The global styles in styles.scss neutralize the invalid (red) look
        // for these fields, so controls never start out red.
        'app-invalid-untouched': f =>
          f.state().invalid() && !f.state().touched(),
      },
    }),
  ],
};
